import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import adminService from '../../services/admin.service';
import { AdminAnalysisListResponse, AdminUserDetail } from '../../types/admin.types';
import ConfirmationDialog from '../../components/shared/ConfirmationDialog';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'queued', label: 'Queued' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  queued: { className: 'bg-yellow-100 text-yellow-800', label: 'Queued' },
  processing: { className: 'bg-blue-100 text-blue-800', label: 'Processing' },
  completed: { className: 'bg-green-100 text-green-800', label: 'Completed' },
  failed: { className: 'bg-red-100 text-red-800', label: 'Failed' },
  uploading: { className: 'bg-gray-100 text-gray-800', label: 'Uploading' },
};

const PAGE_SIZE = 50;

const AdminUserAnalyses: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<AdminAnalysisListResponse | null>(null);
  const [userDetail, setUserDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const [retrying, setRetrying] = useState<string | null>(null);
  const [retryTarget, setRetryTarget] = useState<string | null>(null);

  const statusFilter = searchParams.get('status') || '';
  const search = searchParams.get('search') || '';
  const offset = parseInt(searchParams.get('offset') || '0', 10);

  useEffect(() => {
    if (!id) return;
    adminService.getUserDetail(id).then(setUserDetail).catch(() => {});
  }, [id]);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const result = await adminService.listUserAnalyses(id, {
        status_filter: statusFilter || undefined,
        search: search || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setData(result);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, statusFilter, search, offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (searchInput) {
        params.set('search', searchInput);
      } else {
        params.delete('search');
      }
      params.set('offset', '0');
      setSearchParams(params, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatusFilterParam = (newStatus: string) => {
    const params = new URLSearchParams(searchParams);
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    params.set('offset', '0');
    setSearchParams(params);
  };

  const setOffset = (newOffset: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('offset', String(newOffset));
    setSearchParams(params);
  };

  const handleRetryConfirm = async () => {
    if (!retryTarget) return;
    setRetrying(retryTarget);
    try {
      await adminService.retryAnalysis(retryTarget);
      toast.success('Analysis retry queued');
      fetchData();
    } catch {
      toast.error('Failed to retry analysis');
    } finally {
      setRetrying(null);
      setRetryTarget(null);
    }
  };

  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to={`/admin/users/${id}`}
          className="rounded-md p-2 text-gray-400 hover:text-gray-500 hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {userDetail?.email ? `${userDetail.email}'s Analyses` : 'User Analyses'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">Call analyses for this user</p>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setStatusFilterParam(opt.value)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === opt.value
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search by agent ID, contact ID..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="block w-full rounded-md border-0 py-2 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
        />
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent / Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Error
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Duration
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div className="h-4 bg-gray-200 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : data?.items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                  No analyses found
                </td>
              </tr>
            ) : (
              data?.items.map((item) => {
                const badge = STATUS_BADGE[item.status] || {
                  className: 'bg-gray-100 text-gray-800',
                  label: item.status,
                };
                return (
                  <tr
                    key={item.id}
                    onClick={() =>
                      navigate(`/admin/users/${id}/analyses/${item.id}`)
                    }
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{item.agent_id || '-'}</div>
                      <div className="text-xs text-gray-400">{item.contact_id || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-red-600 max-w-xs truncate">
                      {item.error_message || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDuration(item.duration_seconds)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(item.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {item.status === 'failed' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setRetryTarget(item.id);
                          }}
                          disabled={retrying === item.id}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-indigo-600 ring-1 ring-indigo-200 hover:bg-indigo-50 disabled:opacity-50"
                        >
                          <ArrowPathIcon className="h-3 w-3" />
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {data && (data.has_more || offset > 0) && (
          <nav className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
            <div className="flex flex-1 items-center justify-between">
              <p className="text-sm text-gray-700">
                Showing {offset + 1} - {offset + (data.items.length)}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                  disabled={offset <= 0}
                  className="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  Previous
                </button>
                <button
                  onClick={() => setOffset(offset + PAGE_SIZE)}
                  disabled={!data.has_more}
                  className="relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </nav>
        )}
      </div>

      {/* Retry Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={retryTarget !== null}
        onClose={() => setRetryTarget(null)}
        onConfirm={handleRetryConfirm}
        title="Retry Analysis"
        message="Retry this analysis? It will be re-queued for processing."
        confirmLabel="Retry"
        loading={retrying !== null}
      />
    </div>
  );
};

export default AdminUserAnalyses;
